/**
 * useAuth.ts — Central authentication hook for Blink Relay.
 *
 * Supports both Azure AD/MSAL and email-based magic link authentication.
 * Prioritizes MSAL if available, falls back to the httpOnly relay_session
 * cookie set by the backend on magic-link verify.
 *
 * Local dev bypass: set VITE_SKIP_AUTH=true in .env.local AND ensure the
 * backend also runs with SKIP_AUTH=true. Both flags must be set.
 */

import { useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InteractionStatus } from "@azure/msal-browser";
import { authApi, setTokenGetter, setSessionExpiredHandler } from "@/lib/api";
import { loginRequest } from "@/lib/msalConfig";
import type { Role, User } from "@/lib/types";
import {
  DEFECT_ONLY_ROLES,
  PM_ROLES,
  READ_ONLY_ROLES,
  REVIEWER_ROLES,
  WRITE_CAPABLE_ROLES,
} from "@/lib/constants";
import { hasCookieSession } from "@/lib/auth";

const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === "true";

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const msalLoading = inProgress !== InteractionStatus.None;
  const queryClient = useQueryClient();

  // Email magic-link sessions are detected via a non-httpOnly flag cookie.
  // The actual JWT lives in an httpOnly relay_session cookie — never in JS.
  const isEmailAuthenticated = SKIP_AUTH ? false : hasCookieSession();

  // ── Token injection ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (SKIP_AUTH) {
      setTokenGetter(async () => "dev-skip-auth-token");
      return;
    }

    setTokenGetter(async () => {
      // MSAL path: acquire token silently (auto-refreshes)
      const account = instance.getActiveAccount() ?? accounts[0];
      if (account) {
        try {
          const result = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          return result.accessToken;
        } catch {
          await instance.acquireTokenRedirect({ ...loginRequest, account });
          return null;
        }
      }

      // Cookie-based email session: the relay_session httpOnly cookie is sent
      // automatically by the browser — no token value needed in JS.
      return null;
    });
  }, [instance, accounts]);

  // ── Auth user query ─────────────────────────────────────────────────────────
  const shouldFetchMe = SKIP_AUTH
    ? true
    : (msalAuthenticated && !msalLoading) || isEmailAuthenticated;

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: isAuthError,
  } = useQuery<User>({
    queryKey: ["auth", "me"],
    queryFn: msalAuthenticated && !SKIP_AUTH ? authApi.sync : authApi.me,
    enabled: shouldFetchMe,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // When the auth query fails for a cookie session, fire the backend logout to
  // clear the httpOnly cookie (JS can't delete it directly), then fall through
  // to MSAL login.
  useEffect(() => {
    if (isAuthError && isEmailAuthenticated && !msalAuthenticated) {
      fetch("/api/v1/auth/email/logout", {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
    }
  }, [isAuthError, isEmailAuthenticated, msalAuthenticated]);

  // ── Derived auth state ──────────────────────────────────────────────────────
  const isAuthenticated = SKIP_AUTH
    ? true
    : msalAuthenticated || isEmailAuthenticated;
  const isLoading = SKIP_AUTH
    ? isLoadingUser
    : msalLoading || (shouldFetchMe && isLoadingUser);

  const login = (redirectTo?: string) => {
    if (!SKIP_AUTH) {
      // Guard inline — inProgress is the current render's value from useMsal(),
      // providing a synchronous check that complements the msalBusy guard in callers.
      if (inProgress !== InteractionStatus.None) return;
      instance
        .loginRedirect({
          ...loginRequest,
          redirectStartPage: redirectTo
            ? `${window.location.origin}${redirectTo}`
            : window.location.origin,
        })
        .catch((e: unknown) => {
          // interaction_in_progress means MSAL is already handling a redirect —
          // safe to ignore, it will resolve on its own.
          const code = (e as { errorCode?: string })?.errorCode;
          if (code !== "interaction_in_progress") {
            console.error("[auth] loginRedirect failed:", e);
          }
        });
    }
  };

  const logout = (reason?: "timeout" | "expired") => {
    const isEmailAuth = hasCookieSession();

    queryClient.removeQueries({ queryKey: ["auth"] });

    const reasonParam = reason ? `?reason=${reason}` : "";

    if (isEmailAuth || SKIP_AUTH) {
      // Ask the backend to clear the httpOnly relay_session cookie
      fetch("/api/v1/auth/email/logout", {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
      window.location.href = `/login${reasonParam}`;
    } else {
      // Clear any stale MSAL interaction lock before logging out.
      // If a previous redirect auth flow was interrupted, MSAL leaves
      // interaction.status = "interaction_in_progress" in sessionStorage,
      // which causes logoutRedirect() to throw BrowserAuthError and silently fail.
      Object.keys(sessionStorage)
        .filter((k) => k.includes("interaction.status"))
        .forEach((k) => sessionStorage.removeItem(k));

      // logoutRedirect() is async — use .catch() so a rejection (e.g. another
      // stale lock we missed) still falls back to a hard redirect.
      //
      // account is required for AAD to honor postLogoutRedirectUri: without an
      // id_token_hint identifying which session is ending, AAD can't confirm the
      // logout and shows its own "You signed out" page instead of redirecting
      // back here — the bug behind a stray click landing on login.microsoftonline.com.
      instance
        .logoutRedirect({
          account: instance.getActiveAccount() ?? accounts[0],
          postLogoutRedirectUri: `${window.location.origin}/login${reasonParam}`,
        })
        .catch(() => {
          window.location.href = `/login${reasonParam}`;
        });
    }
  };

  // ── Role helpers ────────────────────────────────────────────────────────────
  const hasRole = (...roles: Role[]): boolean =>
    user?.roles.some((r) => roles.includes(r as Role)) ?? false;

  const isReviewer = hasRole(...REVIEWER_ROLES);
  const isPM = hasRole(...PM_ROLES);
  const isAdmin = hasRole("Admin");

  const hasReadOnlyRole = hasRole(...READ_ONLY_ROLES);
  const canSubmit = hasRole(...WRITE_CAPABLE_ROLES);
  const isReadOnly = hasReadOnlyRole && !canSubmit;
  // True only when the user's sole write capability is WriteDefect (no broader role)
  const isDefectWriter =
    hasRole(...DEFECT_ONLY_ROLES) &&
    !hasRole("Requestor", "PodReviewer", "ProductManager", "Admin");
  const canViewDashboard = isReviewer || hasReadOnlyRole || isDefectWriter;
  const readScopeTypes: string[] | null = (() => {
    if (!isReadOnly) return null;
    const roles = user?.roles ?? [];
    if (roles.includes("ReadOnly")) return null;
    const types: string[] = [];
    if (roles.includes("ReadOnlyFeature")) types.push("Feature");
    if (roles.includes("ReadOnlyDefect")) types.push("Defect");
    return types.length ? types : null;
  })();

  // ── Wire up global 401 → logout redirect ────────────────────────────────────
  const logoutRef = useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  });

  useEffect(() => {
    if (SKIP_AUTH) return;
    setSessionExpiredHandler(() => logoutRef.current("expired"));
    return () => setSessionExpiredHandler(null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    hasRole,
    isReviewer,
    isPM,
    isAdmin,
    isReadOnly,
    canSubmit,
    canViewDashboard,
    readScopeTypes,
    isDefectWriter,
  };
}
